import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { checkAuthStatus } from '@/utils/auth'
import { useAuthStore } from '@/store/authStore'
import { logger } from '@/utils/logger'
import FadeLoader from './FadeLoader'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [loadingProgress, setLoadingProgress] = useState(0)

  // ✅ accessToken을 구독하여 변경 감지
  const accessToken = useAuthStore(state => state.accessToken)

  // 로딩 진행률 애니메이션
  useEffect(() => {
    if (isLoading) {
      setLoadingProgress(0)
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) return prev // 90%에서 멈춤
          return prev + 10
        })
      }, 200)

      return () => clearInterval(interval)
    } else {
      setLoadingProgress(100)
    }
  }, [isLoading])

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true)

      // 디버깅 로그 추가
      logger.debug('[ProtectedRoute] useEffect 실행 - accessToken:', accessToken ? '있음' : '없음')

      const result = await checkAuthStatus()

      if (!result.authenticated) {
        logger.warn('[ProtectedRoute] 인증되지 않음 - 로그인 페이지로 이동')
        navigate('/auth/login')
      } else if (result.needsRedirect && result.redirectPath) {
        logger.info('[ProtectedRoute] 리다이렉트 필요:', result.redirectPath)
        navigate(result.redirectPath)
      } else {
        logger.success('[ProtectedRoute] 인증 확인 완료')
      }

      setIsLoading(false)
    }

    checkAuth()
  }, [navigate, accessToken])

  // 인증된 경우에만 자식 컴포넌트 렌더링
  return (
    <>
      {/* 로딩 화면 */}
      {isLoading && (
        <div className='fixed inset-0 flex items-center justify-center bg-gradient-to-br from-amber-400 to-yellow-300 z-50'>
          <div className='relative w-[120px] h-[120px] flex items-center justify-center'>
            {/* 방사형 페이드 로더 */}
            <FadeLoader size={120} color='#92400e' />
            {/* 스피너 정중앙에 진행률 텍스트 */}
            <div className='absolute inset-0 flex items-center justify-center'>
              <span className='font-bold text-amber-900'>{loadingProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* 메인 콘텐츠 */}
      <div style={{ display: isLoading ? 'none' : 'block' }}>{children}</div>
    </>
  )
}

export default ProtectedRoute
